/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { TagOutlined } from '@ant-design/icons'
import type { ICellRendererParams } from 'ag-grid-community'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import type { UserRegistration } from '@sportninja/common/hooks/useUserRegistrationData'
import RefundIcon from 'src/components/icons/RefundIcon'
import CreditCardClockIcon from 'src/components/ts-components/icons/CreditCardClockIcon'
import Text from 'src/components/ts-components/Text'
import { formatRegistrationMoney } from '../../utils/formatRegistrationMoney'
import {
  getRefundSummary,
  getRegistrationCountry,
  getTotalPaid,
} from '../../utils/registrationRows'
import { CouponTooltip, RefundTooltip } from './PaidTooltips'

/** The amount paid, with refund, deferred payment and coupon markers. */
const PaidCell = ({ data: record }: ICellRendererParams<UserRegistration>) => {
  if (!record) {
    return null
  }
  if (record.is_comp) {
    return <span>--</span>
  }

  const country = getRegistrationCountry(record)
  const refund = getRefundSummary(record)

  return (
    <div css={rowStyle}>
      {refund ? (
        <Tooltip
          placement='top'
          title={<RefundTooltip refund={refund} country={country} />}
        >
          <span css={iconStyle}>
            <RefundIcon size={20} color={colors.INFO_200} />
          </span>
        </Tooltip>
      ) : null}
      {record.defer_payment ? (
        <Tooltip title='Deferred Payment' placement='top'>
          <span css={iconStyle}>
            <CreditCardClockIcon size={16} color={colors.PRIMARY} />
          </span>
        </Tooltip>
      ) : record.is_coupon ? (
        <Tooltip
          placement='top'
          title={
            record.coupon ? (
              <CouponTooltip coupon={record.coupon} country={country} />
            ) : (
              'Coupon'
            )
          }
        >
          <TagOutlined css={couponIconStyle} />
        </Tooltip>
      ) : null}
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        {formatRegistrationMoney(getTotalPaid(record), country)}
      </Text>
    </div>
  )
}

export default PaidCell

// ─── Styles ──────────────────────────────────────────────────────

const rowStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`

const iconStyle = css`
  display: inline-flex;
  align-items: center;
`

const couponIconStyle = css`
  font-size: 16px;
  color: ${colors.PRIMARY};
`
